import { publishGa4Events } from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import { useCitySelectedContext } from '@porterin/feature/src/contexts/city-context/CityContext';
import {
  ImageEntity,
  TextSubtext,
} from '@porterin/feature/src/shared/cities/domain/entities/CitiesEntity';
import {
  filterBasedOnService,
  getRedirectionLink,
} from '@porterin/feature/src/shared/core/helpers/CommonVehicleHelper';
import {
  MODAL_CLOSE_TIMEOUT_ANIMATED,
  ModalControl,
} from '@porterin/feature/src/shared/modal/domain/entities/ModalEntities';
import NextImageV2 from '@porterin/feature/src/ui/components/NextImageV2';
import { updateUserCity } from '@porterin/utils/src/helpers/LocalStorage';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import ReactModal from 'react-modal';

import styles from '../styles/CitySelectorModal.module.scss';

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
};

export interface VehicleSelectorCity {
  city_name: string;
  city_image: ImageEntity;
  city_label: string;
  city_services: Array<{ text: string }>;
}

interface VehicleCitySelectorModalCmpProps {
  modal: ModalControl;
  city_selector_data: VehicleSelectorCity[];
  service_category: unknown;
  city_title_desc: TextSubtext;
  close_icon: ImageEntity;
}

function VehicleCitySelectorModalCmp({
  modal,
  city_selector_data,
  service_category,
  city_title_desc,
  close_icon,
}: Readonly<VehicleCitySelectorModalCmpProps>): React.ReactElement {
  const { locale } = useRouter();
  const citySelectedContext = useCitySelectedContext();
  const serviceFilteredResults: VehicleSelectorCity[] = filterBasedOnService(
    city_selector_data,
    service_category,
  );

  const { modal_state, close_modal } = modal;

  const cityClickHandler = (city: string, city_label: string): void => {
    publishGa4Events(
      citySelectedContext.category,
      'City Selection Dropdown Redirect',
      city,
    );
    citySelectedContext.set_city_selected(city, city_label);
    close_modal();
    updateUserCity(city);
  };

  return (
    <ReactModal
      isOpen={modal_state}
      onRequestClose={close_modal}
      closeTimeoutMS={MODAL_CLOSE_TIMEOUT_ANIMATED}
      style={modalStyles}
      className={styles['city-picker-modal']}
      ariaHideApp={false}
      shouldCloseOnOverlayClick
    >
      <div className={styles['container']}>
        <div className={styles['header-container']}>
          <button
            type="button"
            aria-label="Close"
            className={styles['close-button']}
            onClick={close_modal}
          >
            <NextImageV2
              src={close_icon.path}
              height={14}
              width={14}
              quality={100}
              alt={close_icon.alt_text}
            />
          </button>
          <div className={styles['title']}>{city_title_desc.text}</div>
        </div>
        <div className={styles['cities-list-container']}>
          {serviceFilteredResults.map(city_data => (
            <Link
              key={city_data.city_name}
              href={
                getRedirectionLink(city_data, service_category, locale) +
                citySelectedContext.query_params
              }
              target="_parent"
              onClick={() =>
                cityClickHandler(city_data.city_name, city_data.city_label)
              }
            >
              <div className={styles['city-container']}>
                <div className={styles['city-image']}>
                  <NextImageV2
                    src={city_data.city_image.path}
                    height={80}
                    width={80}
                    quality={100}
                    alt={city_data.city_image.alt_text}
                  />
                </div>
                <div className={styles['city-title']}>
                  {city_data.city_name.charAt(0).toUpperCase() +
                    city_data.city_name.slice(1)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </ReactModal>
  );
}

export default VehicleCitySelectorModalCmp;
